/**
 * Restart Flow Action
 *
 * Legacy action - now mostly handled by provider remount (resetMachine).
 * Only used as fallback for WINDOW mode or manual RESTART events.
 */

import { assign } from "xstate";
import { readKioskNavigationMode } from "../../lib/kiosk-settings";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState action types are complex
  impl: any;
  metadata: ActionMetadata;
}

/**
 * Check if localStorage is available
 *
 * Returns false in:
 * - Private/incognito mode (some browsers)
 * - SSR context
 * - Browser with storage disabled
 */
function isStorageAvailable(): boolean {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear all states and params from storage
 */
function clearAllStates() {
  console.log("[StateStorage] Clearing all stored states...");
  if (!isStorageAvailable()) return;

  const prefix = "kiosk-state:";

  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keys.push(key);
      }
    }

    for (const key of keys) {
      localStorage.removeItem(key);
    }

    console.log(`[StateStorage] Cleared ${keys.length} stored state(s)`);
  } catch (error) {
    console.error("[StateStorage] Failed to clear states:", error);
  }
}

/**
 * Restart Flow Action
 *
 * Clears all user data while preserving session/device info.
 * No longer does page reload - relies on state machine transitions to move back to welcome.
 *
 * Example:
 * ```json
 * {
 *   "event": "FINISH",
 *   "actions": [{ "type": "restartFlow" }],
 *   "target": "welcome"
 * }
 * ```
 */
export const restartFlow: ActionWithMetadata = {
  impl: assign(() => {
    // Legacy fallback: This should rarely be called now since STATE_MACHINE mode
    // uses provider remount (resetMachine()) which creates a brand new machine.
    // Only executed if someone manually sends RESTART event to the machine,
    // or during FINISH transitions in WINDOW mode.
    if (readKioskNavigationMode() === "window") {
      clearAllStates();
      if (typeof window !== "undefined") {
        window.location.href = window.location.pathname;
      }
    }

    // Return empty object (either page reloads or this is a no-op)
    return {};
  }),
  metadata: {
    displayName: "restartFlow",
    description: "Legacy restart action (now handled by provider remount)",
    category: "context",
  },
};
